import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ThemeToggle from './ui/ThemeToggle';
import { useApi } from '../hooks/useApi';
import { usePendingChangesContext } from '../contexts/PendingChangesContext';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const api = useApi();
  const { isDirty, saveHandler, clearHandler } = usePendingChangesContext();

  // Touch gesture state
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const swipeDetected = useRef(false);
  const swipeThreshold = 50; // Lowered for better mobile responsiveness
  const verticalThreshold = 100;

  // Fetch jobs to determine if we should show Table Editor and Insights links
  const { data: jobsData } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const response = await api.get('/api/jobs');
      return response.data;
    },
  });

  const jobs = jobsData?.jobs || [];
  const hasCompletedJobs = jobs.some((job) => job.status === 'completed');

  // Prevent scrolling when sidebar is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.documentElement.classList.add("overflow-hidden");
    } else {
      document.documentElement.classList.remove("overflow-hidden");
    }

    return () => {
      document.documentElement.classList.remove("overflow-hidden");
    };
  }, [mobileMenuOpen]);

  // Touch event handlers for swipe gestures
  useEffect(() => {
    // Define the area on the right edge (in pixels) that can trigger the open swipe
    const edgeThreshold = 100;

    const handleTouchStart = (e) => {
      const startX = e.changedTouches[0].screenX;

      if (mobileMenuOpen) {
        // CLOSE LOGIC - only care about touches that start ON the sidebar
        if (!e.target.closest(".mobile-menu-sidebar")) {
          touchStartX.current = 0; // Invalidate the swipe
          return;
        }
      } else {
        // OPEN LOGIC - only care about touches that start on the RIGHT EDGE
        if (startX < window.innerWidth - edgeThreshold) {
          touchStartX.current = 0; // Invalidate the swipe
          return;
        }
      }

      // If we're here, it's a valid touch start for the current state
      touchStartX.current = startX;
      touchStartY.current = e.changedTouches[0].screenY;
      swipeDetected.current = false;
    };

    const handleTouchMove = (e) => {
      // Don't do anything if the swipe was invalidated or already detected
      if (!touchStartX.current || swipeDetected.current) return;

      const currentX = e.changedTouches[0].screenX;
      const currentY = e.changedTouches[0].screenY;

      const horizontalDiff = currentX - touchStartX.current;
      const verticalDiff = Math.abs(currentY - touchStartY.current);

      // Only trigger if vertical movement is minimal
      if (verticalDiff < verticalThreshold) {
        // Swipe left (open menu) - when not already open
        if (horizontalDiff < -swipeThreshold && !mobileMenuOpen) {
          setMobileMenuOpen(true);
          swipeDetected.current = true;
        }
        // Swipe right (close menu) - when menu is open
        else if (horizontalDiff > swipeThreshold && mobileMenuOpen) {
          setMobileMenuOpen(false);
          swipeDetected.current = true;
        }
      }
    };

    const handleTouchEnd = () => {
      swipeDetected.current = false;
      touchStartX.current = 0; // Reset touch start X on end
    };

    // Add event listeners
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    // Cleanup
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [mobileMenuOpen, verticalThreshold, swipeThreshold]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Navigation handler that checks for pending changes
  const handleNavigation = (e, path) => {
    // If already on the same path, just close menu
    if (location.pathname === path || location.pathname.startsWith(path + '/')) {
      closeMobileMenu();
      return;
    }

    // If there are pending changes, prompt user
    if (isDirty) {
      e.preventDefault();
      const confirmSave = window.confirm(
        'You have unsaved changes. Do you want to save before leaving?'
      );

      if (confirmSave && saveHandler) {
        saveHandler().then(() => {
          closeMobileMenu();
          navigate(path);
        }).catch((err) => {
          console.error('Failed to save:', err);
          const discardAnyway = window.confirm('Failed to save. Discard changes and leave?');
          if (discardAnyway && clearHandler) {
            clearHandler();
            closeMobileMenu();
            navigate(path);
          }
        });
      } else {
        const discard = window.confirm('Discard changes and leave?');
        if (discard) {
          if (clearHandler) clearHandler();
          closeMobileMenu();
          navigate(path);
        }
      }
    } else {
      closeMobileMenu();
    }
  };

  return (
    <>
      <header className="bg-header-bg dark:bg-header-bg-dark border-b border-border-light dark:border-0 fixed top-0 left-0 w-full z-[60] transition-colors duration-200">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          {/* Logo */}
          <Link to="/" onClick={(e) => handleNavigation(e, '/')} className="text-2xl font-bold text-brand-primary dark:text-brand-primary hover:opacity-70 transition">
            Archa
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <div className="space-x-8 font-normal md:tracking-normal text-lg">
              <Link
                to="/"
                onClick={(e) => handleNavigation(e, '/')}
                className={`transition ${
                  isActive('/')
                    ? 'text-brand-primary'
                    : 'text-header-text dark:text-header-text-dark hover:opacity-70'
                }`}
              >
                Home
              </Link>
              {hasCompletedJobs && (
                <>
                  <Link
                    to="/table"
                    onClick={(e) => handleNavigation(e, '/table')}
                    className={`transition ${
                      isActive('/table')
                        ? 'text-brand-primary'
                        : 'text-header-text dark:text-header-text-dark hover:opacity-70'
                    }`}
                  >
                    Table Editor
                  </Link>
                  <Link
                    to="/insights"
                    onClick={(e) => handleNavigation(e, '/insights')}
                    className={`transition ${
                      isActive('/insights')
                        ? 'text-brand-primary'
                        : 'text-header-text dark:text-header-text-dark hover:opacity-70'
                    }`}
                  >
                    Insights
                  </Link>
                </>
              )}
            </div>

            {/* Theme Toggle Button */}
            <ThemeToggle />
          </div>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-5 flex flex-col justify-between">
              <span
                className={`block h-0.5 w-full bg-header-text dark:bg-header-text-dark transition-all duration-300 ${
                  mobileMenuOpen ? "rotate-45 translate-y-[0.5625rem]" : ""
                }`}
              ></span>
              <span
                className={`block h-0.5 w-full bg-header-text dark:bg-header-text-dark transition-all duration-50 ${
                  mobileMenuOpen ? "opacity-0" : ""
                }`}
              ></span>
              <span
                className={`block h-0.5 w-full bg-header-text dark:bg-header-text-dark transition-all duration-300 ${
                  mobileMenuOpen ? "-rotate-45 -translate-y-[0.5625rem]" : ""
                }`}
              ></span>
            </div>
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          mobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobileMenu}
      ></div>

      {/* Mobile Menu Sidebar - Full Width */}
      <div
        className={`mobile-menu-sidebar fixed top-0 right-0 h-full w-full bg-header-bg-light dark:bg-header-bg z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Close Button */}
        <div className="flex justify-end px-4 py-4 border-b border-border-light dark:border-0">
          <button
            onClick={closeMobileMenu}
            className="p-2 hover:bg-surface-hover dark:hover:bg-surface-dark rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span className="block h-0.5 w-full bg-header-text dark:bg-header-text-dark rotate-45 translate-y-2"></span>
              <span className="block h-0.5 w-full bg-header-text dark:bg-header-text-dark -rotate-45 -translate-y-2"></span>
            </div>
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="flex flex-col h-[calc(100vh-65px)] p-6 overflow-y-auto bg-background dark:bg-background-dark">
          {/* Theme Toggle in Sidebar */}
          <div className="flex items-center justify-between pb-6 mb-6 border-b border-border-light dark:border-0">
            <span className="text-base font-semibold text-header-text dark:text-header-text-dark">Theme</span>
            <ThemeToggle className="ml-4" />
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col space-y-4 flex-1">
            <Link
              to="/"
              onClick={(e) => handleNavigation(e, '/')}
              className={`text-lg font-medium py-3 px-4 rounded-lg transition ${
                isActive('/')
                  ? "bg-brand-primary/10 text-brand-primary"
                  : "hover:bg-surface-hover dark:hover:bg-surface-dark text-header-text dark:text-header-text-dark"
              }`}
            >
              Home
            </Link>
            {hasCompletedJobs && (
              <>
                <Link
                  to="/table"
                  onClick={(e) => handleNavigation(e, '/table')}
                  className={`text-lg font-medium py-3 px-4 rounded-lg transition ${
                    isActive('/table')
                      ? "bg-brand-primary/10 text-brand-primary"
                      : "hover:bg-surface-hover dark:hover:bg-surface-dark text-header-text-light dark:text-header-text"
                  }`}
                >
                  Table Editor
                </Link>
                <Link
                  to="/insights"
                  onClick={(e) => handleNavigation(e, '/insights')}
                  className={`text-lg font-medium py-3 px-4 rounded-lg transition ${
                    isActive('/insights')
                      ? "bg-brand-primary/10 text-brand-primary"
                      : "hover:bg-surface-hover dark:hover:bg-surface-dark text-header-text-light dark:text-header-text"
                  }`}
                >
                  Insights
                </Link>
              </>
            )}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;
